<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { GlobeAltIcon } from '@heroicons/vue/24/outline'
import { supportedLocales } from '@/locales/config'
import { useLocaleRoute } from '@/composables/useLocaleRoute'

const { locale } = useI18n()
const { switchLocale } = useLocaleRoute()

const isOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)

const currentLocale = computed(() =>
  supportedLocales.find((l) => l.code === locale.value) || supportedLocales[0],
)

function select(code: string) {
  if (code !== locale.value) {
    switchLocale(code)
  }
  isOpen.value = false
}

function onClickOutside(e: MouseEvent) {
  if (wrapper.value && !wrapper.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <div ref="wrapper" class="relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <GlobeAltIcon class="w-4 h-4" />
      <span>{{ currentLocale.name }}</span>
    </button>

    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg ring-1 ring-black/5 py-1 z-50"
      >
        <button
          v-for="loc in supportedLocales"
          :key="loc.code"
          @click="select(loc.code)"
          class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
          :class="{
            'text-primary-600 font-medium': loc.code === locale,
            'text-gray-700': loc.code !== locale,
          }"
        >
          {{ loc.name }}
        </button>
      </div>
    </Transition>
  </div>
</template>
